import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useProperty } from "@/contexts/PropertyContext";
import { PageHeader } from "@/components/PageHelpers";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, FileText, DollarSign, Send, Copy } from "lucide-react";

export default function OwnerDirectoryPage() {
  const { propertyId } = useProperty();
  const [owners, setOwners] = useState([]);
  const [addOpen, setAddOpen] = useState(false);
  const [genOpen, setGenOpen] = useState(false);
  const [statement, setStatement] = useState(null);
  const [tempPw, setTempPw] = useState(null);
  const [form, setForm] = useState({
    name: "", email: "", phone: "", mgmt_fee_pct: 8, payout_method: "ach",
    units: "", tin: "", address: "",
  });
  const [genForm, setGenForm] = useState({
    owner_id: "",
    period: new Date().toISOString().slice(0, 7),
  });

  const load = async () => {
    if (!propertyId) return;
    try {
      const { data } = await api.get(`/admin/owners?property_id=${propertyId}`);
      setOwners(data.items || []);
    } catch (e) { toast.error("Failed to load owners"); }
  };

  useEffect(() => { load(); }, [propertyId, load]);

  const createOwner = async () => {
    if (!form.name || !form.email) { toast.error("Name and email required"); return; }
    try {
      const { data } = await api.post("/admin/owners", {
        name: form.name, email: form.email, phone: form.phone,
        property_ids: [propertyId],
        units: form.units ? form.units.split(",").map((s) => s.trim()).filter(Boolean) : [],
        mgmt_fee_pct: parseFloat(form.mgmt_fee_pct || 0),
        payout_method: form.payout_method,
        tin: form.tin || null,
        address: form.address || null,
      });
      setTempPw(data.temp_password);
      setAddOpen(false);
      setForm({ name: "", email: "", phone: "", mgmt_fee_pct: 8, payout_method: "ach", units: "", tin: "", address: "" });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const requestW9Bulk = async () => {
    if (!window.confirm("Queue W-9 request emails to all owners missing a TIN?")) return;
    try {
      const { data } = await api.post("/admin/owners/request-w9", { only_missing: true });
      toast.success(`Queued ${data.queued} W-9 request email${data.queued === 1 ? "" : "s"}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const generate = async () => {
    if (!genForm.owner_id) { toast.error("Pick an owner"); return; }
    try {
      const { data } = await api.post("/admin/owners/statements/generate", {
        owner_id: genForm.owner_id,
        property_id: propertyId,
        period: genForm.period,
      });
      setStatement(data);
      toast.success(`Statement generated · Distribution $${data.distribution_amount.toLocaleString()}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const markSent = async () => {
    if (!statement) return;
    const ref = window.prompt("Distribution reference (ACH #, check #)?");
    if (ref === null) return;
    try {
      await api.post(`/admin/owners/statements/${statement.id}/mark-sent`, { distribution_ref: ref });
      toast.success("Distribution marked sent");
      setStatement({ ...statement, distribution_status: "sent", distribution_ref: ref });
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="space-y-6" data-testid="owners-page">
      <PageHeader
        eyebrow="Owner Portal"
        title="Owner Directory & Statements"
        subtitle="Absentee owners get their own login + monthly P&L + distributions."
        actions={
          <div className="flex gap-2">
            <Button onClick={() => setAddOpen(true)} className="gap-2" data-testid="add-owner-btn">
              <Plus size={14} /> Add owner
            </Button>
            <Button onClick={requestW9Bulk} variant="outline" className="gap-2" data-testid="request-w9-btn">
              Request W-9s
            </Button>
            <Button onClick={() => setGenOpen(true)} variant="outline" className="gap-2" data-testid="gen-statement-btn">
              <FileText size={14} /> Generate statement
            </Button>
          </div>
        }
      />

      <Card className="border-white/10 bg-card overflow-hidden">
        <div className="p-3 text-xs uppercase tracking-wider text-muted-foreground border-b border-white/10">
          Owners · {owners.length}
        </div>
        <Table>
          <TableHeader>
            <TableRow className="border-white/10">
              <TableHead>Name</TableHead>
              <TableHead>Email</TableHead>
              <TableHead className="text-center">Units</TableHead>
              <TableHead className="text-right">Monthly Rent</TableHead>
              <TableHead>Mgmt Fee</TableHead>
              <TableHead>Payout</TableHead>
              <TableHead>W-9 / TIN</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {owners.length === 0 && (
              <TableRow className="border-white/5"><TableCell colSpan={7} className="text-center py-8 text-muted-foreground text-sm" data-testid="owners-empty">
                No owners yet. Add one so absentee investors can log in and see their statements.
              </TableCell></TableRow>
            )}
            {owners.map((o) => (
              <TableRow key={o.id} className="border-white/5" data-testid={`owner-row-${o.id}`}>
                <TableCell className="font-medium">{o.name}</TableCell>
                <TableCell className="text-sm text-muted-foreground">{o.email}</TableCell>
                <TableCell className="text-center">{o.active_units || 0}</TableCell>
                <TableCell className="text-right font-mono">${(o.monthly_rent_managed || 0).toLocaleString()}</TableCell>
                <TableCell>{o.mgmt_fee_pct}%</TableCell>
                <TableCell><Badge variant="outline">{o.payout_method || "ach"}</Badge></TableCell>
                <TableCell>
                  {o.tin ? (
                    <Badge variant="outline" className="text-emerald-500 border-emerald-500/40" data-testid={`tin-ok-${o.id}`}>
                      ··{String(o.tin).slice(-4)}
                    </Badge>
                  ) : (
                    <Badge variant="destructive" data-testid={`tin-missing-${o.id}`}>MISSING</Badge>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      {/* Add owner dialog */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="max-w-lg" data-testid="add-owner-dialog">
          <DialogHeader><DialogTitle>Add owner</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-2"><Label>Full name</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="owner-name" /></div>
            <div className="space-y-2"><Label>Email (login)</Label>
              <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="owner-email" /></div>
            <div className="space-y-2"><Label>Phone</Label>
              <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="owner-phone" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2"><Label>Mgmt fee (%)</Label>
                <Input type="number" value={form.mgmt_fee_pct} onChange={(e) => setForm({ ...form, mgmt_fee_pct: e.target.value })} data-testid="owner-mgmt-fee" /></div>
              <div className="space-y-2"><Label>Payout method</Label>
                <Input value={form.payout_method} onChange={(e) => setForm({ ...form, payout_method: e.target.value })} data-testid="owner-payout" /></div>
            </div>
            <div className="space-y-2"><Label>Units (comma-separated)</Label>
              <Input placeholder="4B, 5A" value={form.units} onChange={(e) => setForm({ ...form, units: e.target.value })} data-testid="owner-units" /></div>
            <div className="space-y-2"><Label>TIN (SSN or EIN — for 1099-MISC)</Label>
              <Input placeholder="123-45-6789 or 12-3456789" value={form.tin} onChange={(e) => setForm({ ...form, tin: e.target.value })} data-testid="owner-tin" /></div>
            <div className="space-y-2"><Label>Mailing address (for 1099-MISC)</Label>
              <Input placeholder="123 Main St, Anytown, CA 90210" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="owner-address" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddOpen(false)}>Cancel</Button>
            <Button onClick={createOwner} data-testid="owner-save">Create owner</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Temp password reveal */}
      <Dialog open={!!tempPw} onOpenChange={(v) => !v && setTempPw(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Owner login credentials</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="text-sm text-muted-foreground">Share these with the owner via a secure channel. They can rotate the password on first login.</div>
            <div className="p-3 rounded border border-white/10 bg-muted font-mono text-sm flex justify-between items-center">
              <div><strong data-testid="temp-pw">{tempPw}</strong></div>
              <Button size="sm" variant="outline" onClick={() => { navigator.clipboard.writeText(tempPw); toast.success("Copied"); }}>
                <Copy size={12} className="mr-1" /> Copy
              </Button>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={() => setTempPw(null)}>Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Generate statement */}
      <Dialog open={genOpen} onOpenChange={setGenOpen}>
        <DialogContent className="max-w-md" data-testid="gen-statement-dialog">
          <DialogHeader><DialogTitle>Generate owner statement</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-2"><Label>Owner</Label>
              <select className="w-full border border-white/10 bg-background rounded p-2 text-sm"
                value={genForm.owner_id}
                onChange={(e) => setGenForm({ ...genForm, owner_id: e.target.value })}
                data-testid="gen-owner">
                <option value="">— Select —</option>
                {owners.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
              </select>
            </div>
            <div className="space-y-2"><Label>Period (YYYY-MM)</Label>
              <Input value={genForm.period} onChange={(e) => setGenForm({ ...genForm, period: e.target.value })} data-testid="gen-period" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setGenOpen(false)}>Cancel</Button>
            <Button onClick={generate} data-testid="gen-run">Generate</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Statement result modal */}
      <Dialog open={!!statement} onOpenChange={(v) => !v && setStatement(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="statement-modal">
          {statement && (
            <>
              <DialogHeader>
                <DialogTitle>Statement · {statement.owner_name} · {statement.period}</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  <Card className="p-3 border-white/10 bg-card">
                    <div className="text-[10px] uppercase text-muted-foreground">Gross Income</div>
                    <div className="text-lg font-mono text-emerald-500">${statement.gross_income.toLocaleString()}</div>
                  </Card>
                  <Card className="p-3 border-white/10 bg-card">
                    <div className="text-[10px] uppercase text-muted-foreground">Expenses</div>
                    <div className="text-lg font-mono text-rose-400">${statement.total_expenses.toLocaleString()}</div>
                  </Card>
                  <Card className="p-3 border-white/10 bg-card">
                    <div className="text-[10px] uppercase text-muted-foreground">Mgmt Fee ({statement.mgmt_fee_pct}%)</div>
                    <div className="text-lg font-mono text-rose-400">${statement.mgmt_fee_amount.toLocaleString()}</div>
                  </Card>
                  <Card className="p-3 border-emerald-500/30 bg-emerald-500/5">
                    <div className="text-[10px] uppercase text-muted-foreground">Distribution</div>
                    <div className="text-lg font-mono text-emerald-500" data-testid="statement-distribution">${statement.distribution_amount.toLocaleString()}</div>
                  </Card>
                </div>

                <div>
                  <div className="text-xs uppercase tracking-wider text-muted-foreground mb-2">Income ({statement.income.length})</div>
                  <div className="text-xs space-y-1 max-h-40 overflow-y-auto">
                    {statement.income.map((i, idx) => (
                      <div key={idx} className="flex justify-between border-b border-white/5 py-1">
                        <span><span className="text-muted-foreground">{i.date}</span> {i.description}</span>
                        <span className="font-mono text-emerald-500">+${i.amount.toLocaleString()}</span>
                      </div>
                    ))}
                  </div>
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wider text-muted-foreground mb-2">Expenses ({statement.expenses.length})</div>
                  <div className="text-xs space-y-1 max-h-40 overflow-y-auto">
                    {statement.expenses.map((e, idx) => (
                      <div key={idx} className="flex justify-between border-b border-white/5 py-1">
                        <span><span className="text-muted-foreground">{e.date}</span> {e.description}</span>
                        <span className="font-mono text-rose-400">-${e.amount.toLocaleString()}</span>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="text-xs text-muted-foreground">
                  Status: <Badge variant={statement.distribution_status === "sent" ? "default" : "secondary"}>
                    {statement.distribution_status}
                  </Badge>
                  {statement.distribution_ref && <span className="ml-2">Ref: {statement.distribution_ref}</span>}
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setStatement(null)}>Close</Button>
                <Button variant="outline" className="gap-2" data-testid="statement-download-pdf"
                        onClick={async () => {
                          try {
                            const res = await api.get(`/admin/owners/statements/${statement.id}.pdf`, { responseType: "blob" });
                            const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
                            const a = document.createElement("a");
                            a.href = url; a.download = `owner-statement-${statement.period}.pdf`; a.click();
                            URL.revokeObjectURL(url);
                          } catch { toast.error("PDF download failed"); }
                        }}>
                  <FileText size={12} /> Download PDF
                </Button>
                {statement.distribution_status !== "sent" && (
                  <Button onClick={markSent} data-testid="mark-distribution-sent" className="gap-2">
                    <Send size={12} /> Mark distribution sent
                  </Button>
                )}
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
